// The tornado chart work that runs inside web workers, as plain functions so
// it can be tested without a Worker. See useTornadoChartData for how the
// jobs are scheduled.
import type { DeepMaybeRef } from "@vueuse/core";
import { reactive, ref } from "vue";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import type { HypotheticalFlowDataSet } from "@/libs/calculations/predictiveCalcs/getPredictedDesiredFlowRateSTBD";
import type {
  PumpTornadoChartContext,
  PumpTornadoChartData,
  PumpTornadoChartViewData
} from "../../d";
import { buildTornadoPumpChartContext } from "../../_common/getContext";
import { completePumpChartingData } from "../usePumpChartingData";
import {
  predictFlowRateDataSetAtFrequency,
  type PrecomputedPredictions
} from "../usePredictiveCalcs";
import { decodeUnit, encodeUnits } from "./unitCodec";

// A destructured scenario as plain JSON data (no refs).
export type SerializedScenario = Record<keyof DestructuredScenario, unknown>;

export type TornadoChartUnits = {
  liquidFlow: string;
  power: string;
  largeLength: string;
  pressure: string;
};

export type TornadoChartRequest = {
  scenario: SerializedScenario;
  minFrequency: number;
  maxFrequency: number;
  units: TornadoChartUnits;
  displayRealHzLine?: boolean;
};

// Same deep clone the calculation worker plugin uses: reactive() unwraps the
// refs, and JSON round-tripping drops the Vue proxies structuredClone rejects.
export const serializeScenario = (
  destructuredScenario: DeepMaybeRef<DestructuredScenario>
): SerializedScenario =>
  JSON.parse(JSON.stringify(reactive(destructuredScenario)));

export const deserializeScenario = (
  scenario: SerializedScenario
): DestructuredScenario =>
  Object.fromEntries(
    Object.entries(scenario).map(([key, value]) => [key, ref(value)])
  ) as unknown as DestructuredScenario;

// The frequencies the chart predicts flow rates for: the chart range in 5Hz
// steps plus the scenario's own frequency, as buildTornadoPumpChartContext
// builds them. Known up front so predictions can start before the context.
export const getTornadoChartFrequencies = (
  scenario: SerializedScenario,
  minFrequency: number,
  maxFrequency: number
): number[] => {
  const frequencies: number[] = [];
  for (let f = minFrequency; f <= maxFrequency; f += 5) {
    frequencies.push(f);
  }
  const parameters = scenario.parameters as { pump_hz?: number } | null;
  frequencies.push(parameters?.pump_hz || 60);
  return [...new Set(frequencies)];
};

export const runPredictionJob = (
  scenario: SerializedScenario,
  frequency: number
): HypotheticalFlowDataSet =>
  predictFlowRateDataSetAtFrequency(deserializeScenario(scenario), frequency);

export type TornadoChartJobState = {
  calcs: ScenarioCalculations;
  context: PumpTornadoChartContext | undefined;
};

// First half of the chart job: the context, which includes the expensive
// hypothetical flow rate sweep. Runs while predictions run elsewhere.
export const beginTornadoChartJob = ({
  scenario,
  minFrequency,
  maxFrequency,
  units
}: TornadoChartRequest): TornadoChartJobState => {
  const calcs = useScenarioCalculations(deserializeScenario(scenario));
  const context = buildTornadoPumpChartContext(
    minFrequency,
    maxFrequency,
    calcs,
    decodeUnit({ $unit: units.liquidFlow }),
    decodeUnit({ $unit: units.largeLength }),
    decodeUnit({ $unit: units.pressure }),
    decodeUnit({ $unit: units.power })
  );
  return { calcs, context };
};

export const toViewData = (
  data: PumpTornadoChartData
): PumpTornadoChartViewData => ({
  ...data,
  context: {
    isRateCorrected: data.context.isRateCorrected,
    motorPoles: data.context.motorPoles,
    hypotheticalPumpFrequenciesHz: data.context.hypotheticalPumpFrequenciesHz
  }
});

// Second half: everything else, given the predictions. Any frequency missing
// from predictions is solved here instead. Returns postable data (units
// encoded; see unitCodec).
export const finishTornadoChartJob = (
  { calcs, context }: TornadoChartJobState,
  predictions: PrecomputedPredictions,
  displayRealHzLine?: boolean
): unknown => {
  if (!context || !context.destructuredScenario) return undefined;
  const data = completePumpChartingData(
    context,
    calcs,
    false,
    displayRealHzLine,
    predictions
  );
  return encodeUnits(toViewData(data));
};
